/**
 * Comprehensive Plan Features Configuration
 * 
 * This file defines all features, limits, and capabilities for each subscription plan.
 * Update this file to control what users can do based on their plan.
 */

import { getProMonthlyEmailLimit } from './proEmailLimits';

export type PlanId = 'free' | 'pro' | 'business';

export interface PlanFeatures {
  // Plan identification
  id: PlanId;
  name: string;
  description: string;
  
  // Workspace limits
  maxWorkspaces: number;
  
  // User/Team limits
  maxUsers: number;
  maxTeamMembers: number;
  
  // Contact & Subscriber limits
  maxSubscribers: number;
  maxContacts: number;
  
  // Email limits
  maxEmailsPerMonth: number;
  maxEmailsPerCampaign: number;
  
  // Campaign & Automation limits
  maxCampaigns: number;
  maxActiveCampaigns: number;
  maxAutomations: number;
  maxActiveAutomations: number;
  
  // Landing Pages & Forms
  maxLandingPages: number;
  maxSignupForms: number;
  maxPopups: number;
  
  // Storage limits
  maxStorage: number; // in bytes
  maxFiles: number;
  maxFileSize: number; // in bytes
  
  // Feature flags
  features: {
    // Email features
    customEmailBuilder: boolean;
    emailTemplates: boolean;
    emailScheduling: boolean;
    abTesting: boolean;
    emailAnalytics: boolean;
    
    // Automation features
    advancedAutomations: boolean;
    triggerFilters: boolean;
    goalTracking: boolean;
    automationAnalytics: boolean;
    
    // Design features
    customBranding: boolean;
    removeScubamailBadge: boolean;
    customDomain: boolean;
    whiteLabel: boolean;
    
    // Integration features
    apiAccess: boolean;
    webhooks: boolean;
    customIntegrations: boolean;
    shopifyIntegration: boolean;
    woocommerceIntegration: boolean;
    
    // Support features
    emailSupport: boolean;
    prioritySupport: boolean;
    liveChatSupport: boolean;
    dedicatedAccountManager: boolean;
    
    // Reporting features
    advancedReports: boolean;
    dataExport: boolean;
    customReports: boolean;
    reportHistory: number; // days to keep reports
    
    // Landing page features
    customLandingPages: boolean;
    aBTestingLandingPages: boolean;
    landingPageAnalytics: boolean;
    
    // Form features
    customForms: boolean;
    formAnalytics: boolean;
    formConditionalLogic: boolean;
  };
  
  // Pricing (optional, for reference)
  price?: {
    monthly: number;
    annual: number;
    currency: string;
  };
}

/**
 * Plan Features Configuration
 * Modify these to control what each plan can do
 */
export const PLAN_FEATURES: Record<PlanId, PlanFeatures> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Essential tools to build your audience and send professional newsletters with ease.',
    
    // Workspace limits
    maxWorkspaces: 1,
    
    // User/Team limits
    maxUsers: 1,
    maxTeamMembers: 0,
    
    // Contact & Subscriber limits
    maxSubscribers: 1000,
    maxContacts: 1000,
    
    // Email limits
    maxEmailsPerMonth: 12000,
    maxEmailsPerCampaign: 5000,
    
    // Campaign & Automation limits
    maxCampaigns: Infinity,
    maxActiveCampaigns: Infinity,
    maxAutomations: Infinity,
    maxActiveAutomations: Infinity,
    
    // Landing Pages & Forms
    maxLandingPages: 3,
    maxSignupForms: 3,
    maxPopups: 10,
    
    // Storage limits (100MB)
    maxStorage: 100 * 1024 * 1024,
    maxFiles: 50,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    
    // Feature flags
    features: {
      customEmailBuilder: true, // Enabled for everyone to encourage creation
      emailTemplates: true,
      emailScheduling: true,
      abTesting: true,
      emailAnalytics: true,
      
      advancedAutomations: true,
      triggerFilters: true,
      goalTracking: true,
      automationAnalytics: true,
      
      customBranding: true,
      removeScubamailBadge: false,
      customDomain: true,
      whiteLabel: false,
      
      apiAccess: true,
      webhooks: true,
      customIntegrations: true,
      shopifyIntegration: true,
      woocommerceIntegration: true,
      
      emailSupport: true,
      prioritySupport: false,
      liveChatSupport: false,
      dedicatedAccountManager: false,
      
      advancedReports: true,
      dataExport: true,
      customReports: true,
      reportHistory: Infinity,
      
      customLandingPages: false,
      aBTestingLandingPages: false,
      landingPageAnalytics: true,
      
      customForms: false,
      formAnalytics: true,
      formConditionalLogic: false,
    },
  },
  
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Maximize engagement with advanced automation, deep analytics, and white-labeled emails. (Fair Use Policy applies).',
    
    // Workspace limits
    maxWorkspaces: 3,
    
    // User/Team limits
    maxUsers: 5,
    maxTeamMembers: 5,
    
    // Contact & Subscriber limits (CUSTOMIZED based on user selection in pricing page)
    // These values are overridden at runtime based on subscription.subscribers
    maxSubscribers: 100000, // Base value, overridden by selected contact count
    maxContacts: 100000, // Base value, overridden by selected contact count
    
    // Email limits — Pro uses tiered caps from proEmailLimits.ts when customized
    maxEmailsPerMonth: 12000, // Base for display; overridden by getDynamicPlanFeatures for Pro
    maxEmailsPerCampaign: Infinity,
    
    // Campaign & Automation limits
    maxCampaigns: Infinity,
    maxActiveCampaigns: Infinity,
    maxAutomations: Infinity,
    maxActiveAutomations: Infinity,
    
    // Landing Pages & Forms
    maxLandingPages: 10,
    maxSignupForms: 10,
    maxPopups: Infinity,
    
    // Storage limits (1GB)
    maxStorage: 1 * 1024 * 1024 * 1024,
    maxFiles: 5000,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    
    // Feature flags
    features: {
      customEmailBuilder: true,
      emailTemplates: true,
      emailScheduling: true,
      abTesting: true,
      emailAnalytics: true,
      
      advancedAutomations: true,
      triggerFilters: true,
      goalTracking: true,
      automationAnalytics: true,
      
      customBranding: true,
      removeScubamailBadge: true,
      customDomain: true, // Moved to Pro for better brand identity
      whiteLabel: false,
      
      apiAccess: true,
      webhooks: true,
      customIntegrations: true,
      shopifyIntegration: true,
      woocommerceIntegration: true,
      
      emailSupport: true,
      prioritySupport: false,
      liveChatSupport: false,
      dedicatedAccountManager: false,
      
      advancedReports: true,
      dataExport: true,
      customReports: true,
      reportHistory: Infinity, // Forever
      
      customLandingPages: true,
      aBTestingLandingPages: true,
      landingPageAnalytics: true,
      
      customForms: true,
      formAnalytics: true,
      formConditionalLogic: true,
    },
  },
  
  business: {
    id: 'business',
    name: 'Business',
    description: 'The ultimate scaling engine for high-volume teams requiring 24/7 priority support and enterprise-grade deliverability. (Fair Use Policy applies).',
    
    // Workspace limits
    maxWorkspaces: Infinity, // Unlimited
    
    // User/Team limits
    maxUsers: Infinity,
    maxTeamMembers: Infinity,
    
    // Contact & Subscriber limits (CUSTOMIZED based on user selection in pricing page)
    // For Business plan, if user selects a specific contact range, it's set to that value
    // Otherwise remains unlimited
    maxSubscribers: Infinity, // Base unlimited, but can be customized based on selection
    maxContacts: Infinity, // Base unlimited, but can be customized based on selection
    
    // Email limits — Business is unlimited at runtime
    maxEmailsPerMonth: Infinity,
    maxEmailsPerCampaign: Infinity,
    
    // Campaign & Automation limits
    maxCampaigns: Infinity,
    maxActiveCampaigns: Infinity,
    maxAutomations: Infinity,
    maxActiveAutomations: Infinity,
    
    // Landing Pages & Forms
    maxLandingPages: Infinity,
    maxSignupForms: Infinity,
    maxPopups: Infinity,
    
    // Storage limits (5GB)
    maxStorage: 5 * 1024 * 1024 * 1024,
    maxFiles: 50000,
    maxFileSize: 500 * 1024 * 1024, // 500MB
    
    // Feature flags
    features: {
      customEmailBuilder: true,
      emailTemplates: true,
      emailScheduling: true,
      abTesting: true,
      emailAnalytics: true,
      
      advancedAutomations: true,
      triggerFilters: true,
      goalTracking: true,
      automationAnalytics: true,
      
      customBranding: true,
      removeScubamailBadge: true,
      customDomain: true,
      whiteLabel: true,
      
      apiAccess: true,
      webhooks: true,
      customIntegrations: true,
      shopifyIntegration: true,
      woocommerceIntegration: true,
      
      emailSupport: true,
      prioritySupport: true,
      liveChatSupport: false,
      dedicatedAccountManager: false,
      
      advancedReports: true,
      dataExport: true,
      customReports: true,
      reportHistory: Infinity,
      
      customLandingPages: true,
      aBTestingLandingPages: true,
      landingPageAnalytics: true,
      
      customForms: true,
      formAnalytics: true,
      formConditionalLogic: true,
    },
  },
};

/**
 * Get plan features by plan ID
 */
export function getPlanFeatures(planId: PlanId | string): PlanFeatures {
  const normalizedPlanId = planId.toLowerCase() as PlanId;
  
  // Map legacy plan names to new ones
  const planMapping: Record<string, PlanId> = {
    'starter': 'free',
    'free': 'free',
    'pro': 'pro',
    'business': 'business',
    'enterprise': 'business',
  };
  
  const mappedPlanId = planMapping[normalizedPlanId] || normalizedPlanId;
  
  return PLAN_FEATURES[mappedPlanId] || PLAN_FEATURES.free;
}

/**
 * Check if a feature is available in a plan
 */
export function hasFeature(planId: PlanId | string, feature: keyof PlanFeatures['features']): boolean {
  const planFeatures = getPlanFeatures(planId);
  return planFeatures.features[feature] === true;
}

/**
 * Get a limit value for a plan
 */
export function getPlanLimit(planId: PlanId | string, limit: keyof Omit<PlanFeatures, 'id' | 'name' | 'description' | 'features' | 'price'>): number {
  const planFeatures = getPlanFeatures(planId);
  return planFeatures[limit] as number;
}

/**
 * Check if a value is within plan limit
 */
export function isWithinLimit(
  planId: PlanId | string,
  limit: keyof Omit<PlanFeatures, 'id' | 'name' | 'description' | 'features' | 'price'>,
  currentValue: number
): boolean {
  const planLimit = getPlanLimit(planId, limit);
  
  // Infinity means unlimited
  if (planLimit === Infinity) {
    return true;
  }
  
  return currentValue < planLimit;
}

/**
 * Get remaining capacity for a limit
 */
export function getRemainingCapacity(
  planId: PlanId | string,
  limit: keyof Omit<PlanFeatures, 'id' | 'name' | 'description' | 'features' | 'price'>,
  currentValue: number
): number {
  const planLimit = getPlanLimit(planId, limit);
  
  if (planLimit === Infinity) {
    return Infinity;
  }
  
  return Math.max(0, planLimit - currentValue);
}

/**
 * Get dynamic plan features based on selected subscriber count
 * This is used for Pro and Business plans where limits are customized
 */
export function getDynamicPlanFeatures(
  planId: PlanId,
  subscribers: number
): PlanFeatures {
  const baseFeatures = getPlanFeatures(planId);
  
  // Only Pro and Business plans have dynamic customization
  if (planId !== 'pro' && planId !== 'business') {
    return baseFeatures;
  }
  
  // Customize based on selected subscriber count
  return {
    ...baseFeatures,
    maxSubscribers: subscribers > 0 ? subscribers : baseFeatures.maxSubscribers,
    maxContacts: subscribers > 0 ? subscribers : baseFeatures.maxContacts,
    // Pro: tiered monthly email caps; Business: unlimited
    maxEmailsPerMonth:
      planId === 'business'
        ? Infinity
        : subscribers > 0
          ? getProMonthlyEmailLimit(subscribers)
          : baseFeatures.maxEmailsPerMonth,
  };
}
